"use client";

import { useMemo, useState, type ReactNode } from "react";
import { Check, Search, Tag } from "lucide-react";
import { Popover } from "@/components/ui/Popover";
import { StatusIcon } from "@/components/ui/StatusIcon";
import { PriorityIcon } from "@/components/ui/PriorityIcon";
import { Avatar } from "@/components/ui/Avatar";
import { STATUSES, PRIORITIES } from "@/lib/constants";
import { useStore } from "@/lib/store";
import { usersForWorkspace } from "@/lib/workspaceMembers";
import type { StatusId, PriorityId, User, Label, Project } from "@/lib/types";
import { cn } from "@/lib/utils";

function MenuShell({
  children,
  placeholder,
  query,
  setQuery,
}: {
  children: ReactNode;
  placeholder: string;
  query: string;
  setQuery: (q: string) => void;
}) {
  return (
    <div className="w-[240px] py-1">
      <div className="px-2 py-1.5 border-b border-[var(--border)] flex items-center gap-2">
        <Search size={12} className="text-[var(--fg-tertiary)]" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-transparent outline-none text-[12.5px] placeholder:text-[var(--fg-muted)]"
        />
      </div>
      <div className="max-h-[280px] overflow-y-auto scrollbar-thin py-1">{children}</div>
    </div>
  );
}

function Item({
  onClick,
  selected,
  children,
}: {
  onClick: () => void;
  selected?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-2 px-2 py-1.5 text-[12.5px] hover:bg-[var(--bg-hover)] text-left transition-colors"
      )}
    >
      {children}
      {selected && <Check size={13} className="ml-auto text-[var(--fg-secondary)]" />}
    </button>
  );
}

export function StatusPicker({
  value,
  onChange,
  trigger,
}: {
  value: StatusId;
  onChange: (v: StatusId) => void;
  trigger: ReactNode;
}) {
  const [q, setQ] = useState("");
  return (
    <Popover trigger={trigger}>
      {(close) => (
        <MenuShell placeholder="Change status…" query={q} setQuery={setQ}>
          {STATUSES.filter((s) => s.name.toLowerCase().includes(q.toLowerCase())).map((s) => (
            <Item
              key={s.id}
              selected={value === s.id}
              onClick={() => {
                onChange(s.id);
                close();
              }}
            >
              <StatusIcon status={s.id} size={13} />
              <span>{s.name}</span>
            </Item>
          ))}
        </MenuShell>
      )}
    </Popover>
  );
}

export function PriorityPicker({
  value,
  onChange,
  trigger,
}: {
  value: PriorityId;
  onChange: (v: PriorityId) => void;
  trigger: ReactNode;
}) {
  const [q, setQ] = useState("");
  return (
    <Popover trigger={trigger}>
      {(close) => (
        <MenuShell placeholder="Change priority…" query={q} setQuery={setQ}>
          {PRIORITIES.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())).map((p) => (
            <Item
              key={p.id}
              selected={value === p.id}
              onClick={() => {
                onChange(p.id);
                close();
              }}
            >
              <PriorityIcon priority={p.id} size={13} />
              <span>{p.name}</span>
            </Item>
          ))}
        </MenuShell>
      )}
    </Popover>
  );
}

export function AssigneePicker({
  value,
  onChange,
  trigger,
  unassignedLabel = "Unassigned",
  kind = "all",
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  trigger: ReactNode;
  unassignedLabel?: string;
  kind?: "all" | "human" | "agent";
}) {
  const users = useStore((s) => s.users);
  const workspaceMembers = useStore((s) => s.workspaceMembers);
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const [q, setQ] = useState("");
  const activeWorkspaceUsers = useMemo(
    () => usersForWorkspace(users, workspaceMembers, activeWorkspaceId),
    [activeWorkspaceId, users, workspaceMembers]
  );
  const filtered = activeWorkspaceUsers.filter((u: User) => {
    if (kind === "human" && u.isBot) return false;
    if (kind === "agent" && !u.isBot) return false;
    return `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase());
  });
  // Humans first, then agents — keeps the picker scannable when many
  // bots exist in the workspace (one per integration).
  const humans = filtered.filter((u) => !u.isBot);
  const bots = filtered.filter((u) => u.isBot);
  return (
    <Popover trigger={trigger}>
      {(close) => (
        <MenuShell
          placeholder={
            kind === "human"
              ? "Search people…"
              : kind === "agent"
                ? "Search agents…"
                : "Search people or agents…"
          }
          query={q}
          setQuery={setQ}
        >
          <Item
            selected={value === null}
            onClick={() => {
              onChange(null);
              close();
            }}
          >
            <Avatar user={null} size={18} />
            <span className="text-[var(--fg-secondary)]">{unassignedLabel}</span>
          </Item>
          {humans.length > 0 && (
            <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[var(--fg-muted)] font-medium">
              People
            </div>
          )}
          {humans.map((u) => (
            <Item
              key={u.id}
              selected={value === u.id}
              onClick={() => {
                onChange(u.id);
                close();
              }}
            >
              <Avatar user={u} size={18} />
              <span>{u.name}</span>
            </Item>
          ))}
          {bots.length > 0 && (
            <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[var(--fg-muted)] font-medium">
              Agents
            </div>
          )}
          {bots.map((u) => (
            <Item
              key={u.id}
              selected={value === u.id}
              onClick={() => {
                onChange(u.id);
                close();
              }}
            >
              <Avatar user={u} size={18} />
              <span>{u.name}</span>
              <span className="ml-auto text-[10px] text-[var(--green)]">bot</span>
            </Item>
          ))}
        </MenuShell>
      )}
    </Popover>
  );
}

export function LabelPicker({
  teamId,
  value,
  onChange,
  trigger,
}: {
  teamId: string;
  value: string[];
  onChange: (v: string[]) => void;
  trigger: ReactNode;
}) {
  const labels = useStore((s) => s.labels);
  const [q, setQ] = useState("");
  const teamLabels = labels.filter((l: Label) => l.teamId === teamId);
  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };
  return (
    <Popover trigger={trigger}>
      {() => (
        <MenuShell placeholder="Add labels…" query={q} setQuery={setQ}>
          {teamLabels
            .filter((l) => l.name.toLowerCase().includes(q.toLowerCase()))
            .map((l) => (
              <Item key={l.id} selected={value.includes(l.id)} onClick={() => toggle(l.id)}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} />
                <span>{l.name}</span>
              </Item>
            ))}
          {teamLabels.length === 0 && (
            <div className="px-3 py-3 text-[11.5px] text-[var(--fg-muted)] flex items-center gap-2">
              <Tag size={12} />
              No labels for this team yet
            </div>
          )}
        </MenuShell>
      )}
    </Popover>
  );
}

export function ProjectPicker({
  value,
  onChange,
  trigger,
  teamId,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  trigger: ReactNode;
  teamId?: string;
}) {
  const projects = useStore((s) => s.projects);
  const [q, setQ] = useState("");
  const filtered = projects.filter((p: Project) => {
    if (teamId && !p.teamIds.includes(teamId)) return false;
    return p.name.toLowerCase().includes(q.toLowerCase());
  });
  return (
    <Popover trigger={trigger}>
      {(close) => (
        <MenuShell placeholder="Add to project…" query={q} setQuery={setQ}>
          <Item
            selected={value === null}
            onClick={() => {
              onChange(null);
              close();
            }}
          >
            <span className="w-4 text-center text-[var(--fg-muted)]">—</span>
            <span className="text-[var(--fg-secondary)]">No project</span>
          </Item>
          {filtered.map((p) => (
            <Item
              key={p.id}
              selected={value === p.id}
              onClick={() => {
                onChange(p.id);
                close();
              }}
            >
              <span>{p.icon}</span>
              <span className="truncate">{p.name}</span>
            </Item>
          ))}
        </MenuShell>
      )}
    </Popover>
  );
}

export function TeamPicker({
  value,
  onChange,
  trigger,
}: {
  value: string;
  onChange: (v: string) => void;
  trigger: ReactNode;
}) {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const allTeams = useStore((s) => s.teams);
  const teams = useMemo(
    () => allTeams.filter((t) => t.workspaceId === activeWorkspaceId),
    [allTeams, activeWorkspaceId]
  );
  const [q, setQ] = useState("");
  return (
    <Popover trigger={trigger}>
      {(close) => (
        <MenuShell placeholder="Team…" query={q} setQuery={setQ}>
          {teams
            .filter((t) => t.name.toLowerCase().includes(q.toLowerCase()))
            .map((t) => (
              <Item
                key={t.id}
                selected={value === t.id}
                onClick={() => {
                  onChange(t.id);
                  close();
                }}
              >
                <span
                  className="w-4 h-4 rounded-[4px] flex items-center justify-center text-[10px]"
                  style={{ background: `${t.color}22`, color: t.color }}
                >
                  {t.icon}
                </span>
                <span>{t.name}</span>
              </Item>
            ))}
        </MenuShell>
      )}
    </Popover>
  );
}
